import { useState } from "react";
import { PageHeader } from "@/components/StatCard";
import { Stat, Section, Loading, Empty, Pill, useFetch } from "./_shared";

const RANGES = [
  { d: 7, label: "7 Tage" },
  { d: 30, label: "30 Tage" },
  { d: 90, label: "90 Tage" },
];

function num(n) {
  return (Number(n) || 0).toLocaleString("de-DE");
}

function Bars({ series }) {
  if (!series?.length) return <Empty text="Noch keine Zeitreihen-Daten." />;
  const max = Math.max(1, ...series.map((p) => p.web_pageviews + p.mobile_opens));
  return (
    <div className="flex items-end gap-0.5 overflow-x-auto pb-1" data-testid="admin-traffic-timeseries">
      {series.map((p) => {
        const total = p.web_pageviews + p.mobile_opens;
        return (
          <div key={p.date} className="flex min-w-[6px] flex-1 flex-col items-center gap-1" title={`${p.date}: ${num(total)} Aufrufe`}>
            <div className="flex w-full flex-col justify-end" style={{ height: 120 }}>
              <div className="w-full rounded-sm bg-sky-500" style={{ height: `${Math.round((p.web_pageviews / max) * 100)}%` }} />
              <div className="w-full rounded-b-sm bg-violet-400" style={{ height: `${Math.round((p.mobile_opens / max) * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Funnel({ data }) {
  if (!data?.stages?.length) return <Empty text="Noch keine Funnel-Daten." />;
  const maxV = Math.max(1, ...data.stages.map((s) => s.value || 0));
  const colors = ["bg-sky-500", "bg-violet-500", "bg-emerald-500"];
  return (
    <div className="space-y-3" data-testid="admin-funnel">
      {data.stages.map((s, i) => {
        const w = Math.max(6, Math.round(((s.value || 0) / maxV) * 100));
        return (
          <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
            <div className="mb-1 flex items-center justify-between gap-2 text-sm">
              <span className="font-medium text-foreground">{s.label}</span>
              <span className="flex items-center gap-2">
                {i > 0 && s.pct_of_prev != null && (
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
                    {s.pct_of_prev}% Conversion
                  </span>
                )}
                <span className="font-semibold text-foreground">{num(s.value)}</span>
              </span>
            </div>
            <div className="h-8 w-full overflow-hidden rounded-lg bg-secondary">
              <div className={`h-full rounded-lg ${colors[i % colors.length]}`} style={{ width: `${w}%` }} />
            </div>
            {s.breakdown && (
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`funnel-breakdown-${s.key}`}>
                mobil (RevenueCat): <span className="font-semibold text-foreground">{num(s.breakdown.mobile_revenuecat)}</span>
                {" · "}web (Stripe): <span className="font-semibold text-foreground">{num(s.breakdown.web_stripe)}</span>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Traffic() {
  const [days, setDays] = useState(30);
  const { data, loading } = useFetch(`/admin/analytics/traffic?days=${days}`);
  const { data: funnel } = useFetch(`/admin/analytics/funnel?days=${days}`);

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin · Technik & Betrieb" title="Traffic" subtitle="Zugriffe auf my-within.app, my-within.com & Mobile-App – via PostHog." />

      <div className="flex flex-wrap items-center gap-2" data-testid="admin-traffic-ranges">
        {RANGES.map((r) => (
          <button
            key={r.d}
            onClick={() => setDays(r.d)}
            data-testid={`admin-traffic-range-${r.d}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${days === r.d ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : !data ? (
        <Empty text="Traffic-Daten konnten nicht geladen werden." />
      ) : !data.configured ? (
        <Section title="PostHog nicht verbunden" testId="admin-traffic-unconfigured">
          <p className="text-sm text-muted-foreground">{data.reason || "PostHog ist nicht konfiguriert."}</p>
        </Section>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-traffic-kpis">
            <Stat label="Web-Seitenaufrufe" value={num(data.totals.web_pageviews)} tone="default" testId="admin-traffic-web-pageviews" />
            <Stat label="Web-Besucher" value={num(data.totals.web_visitors)} hint={`${num(data.totals.web_sessions)} Sitzungen`} testId="admin-traffic-web-visitors" />
            <Stat label="App-Starts" value={num(data.totals.mobile_opens)} testId="admin-traffic-mobile-opens" />
            <Stat label="Mobile-Nutzer" value={num(data.totals.mobile_users)} hint={`${num(data.mobile?.sessions)} Sitzungen`} testId="admin-traffic-mobile-users" />
          </div>

          <Section title="Akquise-Funnel · Install → Signup → Kauf" testId="admin-funnel-section"
            action={funnel?.posthog_configured === false ? <Pill tone="warn">PostHog n/a</Pill> : null}>
            <Funnel data={funnel} />
            {funnel?.freemium_anon != null && (
              <p className="mt-3 text-xs text-muted-foreground">
                Konten im Zeitraum: <span className="font-semibold text-foreground">{num(funnel.accounts_total)}</span> gesamt —
                davon <span className="font-semibold text-foreground">{num(funnel.signups_real)}</span> echte Registrierungen &amp;
                <span className="font-semibold text-foreground"> {num(funnel.freemium_anon)}</span> anonyme Freemium-Konten (Gastmodus).
                {funnel.opens != null && <> · {num(funnel.opens)} eindeutige App-Öffnungen.</>}
              </p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">
              Installs aus PostHog (mobil, eindeutige Personen) · Konten &amp; Kauf aus der Produktions-DB.
              „Registrierung“ zählt nur echte Konten (ohne anonyme Freemium-Konten). „Kauf“ zählt ALLE zahlenden
              Kanäle (RevenueCat mobil + Stripe web). Registrierungen umfassen Web + Mobile — die
              Install→Signup-Conversion ist daher eine Näherung und kann über 100 % liegen.
            </p>
          </Section>

          {funnel?.signup_reconciliation && (
            <Section title="Abgleich der Zählweisen · neue Nutzer" testId="admin-signup-recon">
              <div className="space-y-2">
                {funnel.signup_reconciliation.sources.map((src, i) => (
                  <div key={i} className="rounded-xl border border-border bg-background p-3" data-testid={`signup-recon-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">{src.source}</p>
                      <span className="shrink-0 text-sm font-semibold text-foreground">
                        {src.value == null ? "nicht vergleichbar" : num(src.value)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{src.metric}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">{src.definition}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-900" data-testid="signup-recon-notes">
                {funnel.signup_reconciliation.notes.map((n, i) => (
                  <p key={i} className={i === 0 ? "font-semibold" : ""}>{n}</p>
                ))}
              </div>
            </Section>
          )}

          <Section title="Zeitverlauf" testId="admin-traffic-timeseries-section"
            action={<div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-sky-500" />Web</span><span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-violet-400" />Mobile</span></div>}>
            <Bars series={data.timeseries} />
          </Section>

          <Section title="Domains" testId="admin-traffic-domains">
            {data.domains?.length ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {data.domains.map((dmn) => (
                  <div key={dmn.host} className="rounded-xl border border-border bg-background p-3" data-testid={`admin-traffic-domain-${dmn.host}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-foreground">{dmn.host}</p>
                      {dmn.is_primary && <Pill tone="good">Produktion</Pill>}
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                      <div><p className="font-display text-lg font-semibold text-foreground">{num(dmn.pageviews)}</p><p className="text-[11px] text-muted-foreground">Aufrufe</p></div>
                      <div><p className="font-display text-lg font-semibold text-foreground">{num(dmn.visitors)}</p><p className="text-[11px] text-muted-foreground">Besucher</p></div>
                      <div><p className="font-display text-lg font-semibold text-foreground">{num(dmn.sessions)}</p><p className="text-[11px] text-muted-foreground">Sitzungen</p></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty text="Noch keine Web-Zugriffe erfasst. Daten sammeln sich ab jetzt." />
            )}
          </Section>

          <div className="grid gap-5 lg:grid-cols-2">
            <Section title="Top-Seiten" testId="admin-traffic-pages">
              {data.top_pages?.length ? (
                <div className="space-y-1.5">
                  {data.top_pages.map((p, i) => (
                    <div key={`${p.host}${p.path}${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2">
                      <div className="min-w-0"><p className="truncate text-sm text-foreground">{p.path}</p><p className="truncate text-[11px] text-muted-foreground">{p.host}</p></div>
                      <span className="shrink-0 text-sm font-semibold text-foreground">{num(p.views)}</span>
                    </div>
                  ))}
                </div>
              ) : <Empty text="Keine Daten." />}
            </Section>

            <Section title="Top-Quellen (Referrer)" testId="admin-traffic-referrers">
              {data.top_referrers?.length ? (
                <div className="space-y-1.5">
                  {data.top_referrers.map((r, i) => (
                    <div key={`${r.ref}${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2">
                      <p className="truncate text-sm text-foreground">{r.ref}</p>
                      <span className="shrink-0 text-sm font-semibold text-foreground">{num(r.visitors)}</span>
                    </div>
                  ))}
                </div>
              ) : <Empty text="Keine Daten." />}
            </Section>

            <Section title="Länder" testId="admin-traffic-countries">
              {data.top_countries?.length ? (
                <div className="space-y-1.5">
                  {data.top_countries.map((c, i) => (
                    <div key={`${c.country}${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2">
                      <p className="truncate text-sm text-foreground">{c.country}</p>
                      <span className="shrink-0 text-sm font-semibold text-foreground">{num(c.visitors)}</span>
                    </div>
                  ))}
                </div>
              ) : <Empty text="Keine Daten." />}
            </Section>

            <Section title="Geräte & OS" testId="admin-traffic-devices">
              {data.devices?.length ? (
                <div className="space-y-1.5">
                  {data.devices.map((dv, i) => (
                    <div key={`${dv.device}${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2">
                      <p className="truncate text-sm capitalize text-foreground">{dv.device}</p>
                      <span className="shrink-0 text-sm font-semibold text-foreground">{num(dv.visitors)}</span>
                    </div>
                  ))}
                </div>
              ) : <Empty text="Keine Daten." />}
            </Section>
          </div>

          <p className="text-center text-[11px] text-muted-foreground">Stand: {data.generated_at ? new Date(data.generated_at).toLocaleString("de-DE") : "—"} · Quelle: PostHog EU</p>
        </>
      )}
    </div>
  );
}
